import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { updateSettings } from '../api/settingsAPI';
import { getAll } from '../api';
import { fetchData } from '../utils/apiHelpers';

const SettingListLayer = () => {
  const userProfile = useUserProfile();
  const [settingList, setSettingList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingValues, setEditingValues] = useState({});
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [collapsedCategories, setCollapsedCategories] = useState({});

  const categoryLabels = useMemo(() => {
    const labels = new Set();
    settingList.forEach((item) => {
      labels.add(item.category?.trim() || 'Uncategorized');
    });
    return Array.from(labels);
  }, [settingList]);

  const handleExpandAll = useCallback(() => {
    setCollapsedCategories(() => {
      const updated = {};
      categoryLabels.forEach((label) => {
        updated[label] = false;
      });
      return updated;
    });
  }, [categoryLabels]);

  const handleCollapseAll = useCallback(() => {
    setCollapsedCategories(() => {
      const updated = {};
      categoryLabels.forEach((label) => {
        updated[label] = true;
      });
      return updated;
    });
  }, [categoryLabels]);

  const fetchSettings = async () => {
    try {
      const data = await fetchData(getAll, { apiName: 'settings/by-org' });
      const sortedData = [...data].sort((a, b) => {
        const categoryA = (a.category || '').toLowerCase();
        const categoryB = (b.category || '').toLowerCase();
        if (categoryA < categoryB) return -1;
        if (categoryA > categoryB) return 1;
        return a.name.localeCompare(b.name);
      });
      setSettingList(sortedData);
      sortedData.forEach((s) => {
        if (s.type === 'dropdown' && s.options) {
          fetchData(getAll, { apiName: s.options }, (resp) => {
            setDropdownOptions((prev) => ({
              ...prev,
              [s.id]: resp.map((r) => ({
                value: r.id,
                label: r.name || r.costCenterDescription || r.id,
              })),
            }));
          });
        }
      });
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };


  const readOnly = !userProfile?.privileges?.includes('settings:update');

  useEffect(() => {
    fetchSettings();
  }, []);

  const filteredSettings = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return settingList;
    }
    return settingList.filter((item) => {
      const description = item.description?.toLowerCase() || '';
      const name = item.name?.toLowerCase() || '';
      const category = item.category?.toLowerCase() || '';
      return (
        description.includes(term) ||
        name.includes(term) ||
        category.includes(term)
      );
    });
  }, [searchTerm, settingList]);

  const renderInput = (item) => {
    const val = editingValues[item.id] ?? (
      item.value === 'true' ? true : item.value === 'false' ? false : item.value
    );

    const handleLocalChange = (newValue) => {
      setEditingValues((prev) => ({ ...prev, [item.id]: newValue }));
    };

    const handleConfirmUpdate = async () => {
      try {
        await updateSettings(item.id, { value: val.toString() });
        setSettingList((prev) =>
          prev.map((s) => (s.id === item.id ? { ...s, value: val.toString() } : s))
        );
        setEditingValues((prev) => {
          const updated = { ...prev };
          delete updated[item.id];
          return updated;
        });
      } catch (err) {
        console.error('Update failed:', err);
      }
    };

    switch (item.type) {
      case 'checkbox':
        return (
          <div className="form-switch switch-primary d-flex align-items-center gap-3 mt-10">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id={`setting-switch-${item.id}`}
              checked={val === true}
              disabled={readOnly}
              onChange={(e) => {
                const newVal = e.target.checked;
                handleLocalChange(newVal);
                updateSettings(item.id, { value: newVal.toString() })
                  .then(() => {
                    setSettingList((prev) =>
                      prev.map((s) =>
                        s.id === item.id ? { ...s, value: newVal.toString() } : s
                      )
                    );
                    setEditingValues((prev) => {
                      const updated = { ...prev };
                      delete updated[item.id];
                      return updated;
                    });
                  })
                  .catch((err) =>
                    console.error('Immediate update failed for checkbox:', err)
                  );
              }}
            />
            <label className="form-check-label" htmlFor={`setting-switch-${item.id}`}>
              {val ? 'Yes' : 'No'}
            </label>
          </div>
        );

      case 'dropdown':
        const opts = dropdownOptions[item.id] || [];
        return (
          <div className="d-flex gap-2">
            <select
              className="form-select"
              value={val || ''}
              onChange={(e) => handleLocalChange(e.target.value)}
              disabled={readOnly}
            >
              <option value="">Select...</option>
              {opts.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {userProfile?.privileges?.includes('settings:update') && (
              <button
                className="w-40-px h-32-px bg-primary-focus text-primary-main rounded-circle d-inline-flex align-items-center justify-content-center"
                style={{ marginTop: '5px' }}
                onClick={handleConfirmUpdate}
                disabled={val.toString() === item.value}
              >
                <Icon icon="mage:save-floppy" className="icon text-xl line-height-1" />
              </button>
            )}
          </div>
        );

      case 'text':
      default:
        return (
          <div className="d-flex gap-2">
            <input
              type="text"
              className="form-control"
              value={val}
              onChange={(e) => handleLocalChange(e.target.value)}
              disabled={readOnly}
            />
            {userProfile?.privileges?.includes('settings:update') && (
              <button
                className="w-40-px h-32-px bg-primary-focus text-primary-main rounded-circle d-inline-flex align-items-center justify-content-center" 
                style={{ marginTop: '5px' }}
                onClick={handleConfirmUpdate}
                disabled={val.toString() === item.value}
              >
                <Icon 
                  icon="mage:save-floppy" 
                  className="icon text-xl line-height-1" 
                />
              </button>
      )}
          </div>
        );
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center gap-3 p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="d-flex align-items-center gap-2 ms-auto">
          <button
            type="button"
            className="btn btn-secondary-outline"
            onClick={handleExpandAll}
          >
            Expand All
          </button>
          <button
            type="button"
            className="btn btn-secondary-outline"
            onClick={handleCollapseAll}
          >
            Collapse All
          </button>
        </div>
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0">
            <thead>
              <tr>
                <th>Setting</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const rows = [];
                let lastCategory = null;
                let categoryIndex = 0;
                filteredSettings.forEach((item) => {
                  const categoryLabel = item.category?.trim() || 'Uncategorized';
                  if (categoryLabel !== lastCategory) {
                    const currentCategoryIndex = categoryIndex;
                    const isCategoryCollapsed = collapsedCategories[categoryLabel] ?? false;
                    rows.push(
                      <tr key={`category-${currentCategoryIndex}`} className="table-light">
                        <td colSpan={2} className="fw-semibold text-uppercase">
                          <div className="d-flex align-items-center justify-content-between">
                            <span>{categoryLabel}</span>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-decoration-none text-dark d-flex align-items-center gap-1"
                              onClick={() =>
                                setCollapsedCategories((prev) => ({
                                  ...prev,
                                  [categoryLabel]: !isCategoryCollapsed,
                                }))
                              }
                              aria-expanded={!isCategoryCollapsed}
                            >
                              {/*<span>{isCategoryCollapsed ? 'Expand' : 'Collapse'}</span>*/}
                              <Icon
                                icon={isCategoryCollapsed ? 'mdi:chevron-right' : 'mdi:chevron-down'}
                                className="icon text-xl"
                              />
                            </button>
                          </div>
                        </td>
                      </tr>,
                    );
                    lastCategory = categoryLabel;
                    categoryIndex += 1;
                    if (isCategoryCollapsed) {
                      return;
                    }
                }
                  const isCategoryCollapsed = collapsedCategories[categoryLabel] ?? false;
                  if (!isCategoryCollapsed) {
                    rows.push(
                      <tr key={item.id}>
                        <td>{item.description}</td>
                        <td>{renderInput(item)}</td>
                      </tr>,
                    );
                  }
                });
                if (rows.length === 0) {
                  rows.push(
                    <tr key="no-results">
                      <td colSpan={2} className="text-center py-4">
                        No settings match your search.
                      </td>
                    </tr>,
                  );
                }
                return rows;
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SettingListLayer;
